import { API_ENDPOINTS } from '../config/api';

class GameService {
  constructor() {
    this.baseURL = '';
  }

  /**
   * Launch a game and get the game URL
   * @param {string} gameType - The type of game (aviator, dice, goal, hilo, mines, plinko)
   * @param {Object} userData - User data containing id and balance
   * @param {number} transferAmount - Amount to transfer (optional, defaults to user balance)
   * @returns {Promise<Object>} Game launch response
   */
  async launchGame(gameType, userData = null, transferAmount = null) {
    try {
      console.log('🚀 Launching game:', { gameType, userData, transferAmount });
      console.log('🌐 API Endpoint:', API_ENDPOINTS.GAMES_LAUNCH);
      
      // Get authentication token
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) {
        throw new Error('Authentication required. Please login to play games.');
      }

      // Get user data from localStorage if not provided
      if (!userData) {
        const userStr = localStorage.getItem('reddyannabook_user');
        if (!userStr) {
          throw new Error('User data not found. Please login again.');
        }
        userData = JSON.parse(userStr);
      }

      // Use user's balance as transfer amount if not specified
      if (transferAmount === null) {
        transferAmount = userData.balance || 0;
      }

      console.log('👤 User data:', { 
        userId: userData.id, 
        balance: userData.balance, 
        transferAmount 
      });
      
      const response = await fetch(`${API_ENDPOINTS.GAMES_LAUNCH}?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          game_type: gameType,
          user_id: userData.id,
          transfer_amount: transferAmount
        })
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', [...response.headers.entries()]);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      console.log('📋 Content-Type:', contentType);
      
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        console.log('📦 Response data:', data);
      } else {
        // If not JSON, get text to see what we're receiving
        const text = await response.text();
        console.error('❌ Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        console.error('❌ API Error:', data);
        
        if (response.status === 401) {
          throw new Error('Unable to load. Please refresh.');
        }
        
        throw new Error(data.error || 'Failed to launch game');
      }

      console.log('✅ Game launch successful:', data);
      return data;
    } catch (error) {
      console.error('💥 Game launch error:', error);
      throw error;
    }
  }

  /**
   * Get available games
   * @returns {Promise<Object>} Available games list
   */
  async getAvailableGames() {
    try {
      const response = await fetch(API_ENDPOINTS.GAMES_LAUNCH);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get games');
      }

      return data;
    } catch (error) {
      console.error('Get games error:', error);
      throw error;
    }
  }

  /**
   * Game type mapping for frontend
   */
  static getGameTypeFromLink(link) {
    const gameTypeMap = {
      'aviator': 'aviator',
      'mines': 'mines',
      'fun-games': 'fun-games',
      'colour-game': 'color-prediction'
    };

    // Extract game type from link
    const url = new URL(link, window.location.origin);
    const menuName = url.searchParams.get('menuName');
    
    return gameTypeMap[menuName] || 'aviator'; // Default to aviator
  }

  /**
   * Game title mapping - Complete HUIDU Gaming Integration (891+ games)
   */
  static getGameTitle(gameType) {
    // If it's already a proper title, return it
    if (gameType && gameType.length > 0 && gameType[0] === gameType[0].toUpperCase()) {
      return gameType;
    }

    // Convert slug to title
    const title = gameType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return title || 'Game';
  }
}

// Create instance for instance methods
const gameServiceInstance = new GameService();

// Add static methods to the instance
gameServiceInstance.getGameTitle = GameService.getGameTitle;
gameServiceInstance.getGameTypeFromLink = GameService.getGameTypeFromLink;

export default gameServiceInstance;
